import { useState, useEffect, useRef } from 'react';
import { Card, Radio, Spin, Empty, Typography, Row, Col } from 'antd';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import DashboardBoxItem from '../Items/Box/DashboardBoxItem';
import OnlineUsersIcon from '../../assets/svgs/IconsBlack/OnlineUsersBlack.svg';
import moment from 'moment';

interface StatsData {
  date: string;
  count: number;
}

interface StatsResponse {
  filterType: '7days' | '30days' | 'lastyear';
  stats: StatsData[];
}

interface SiteWiseUser {
  siteName: string;
  intPkCompanySiteID: number;
  connectedUsers: number;
}

interface ConnectedUsersResponse {
  totalConnectedUsers: number;
  siteWiseUsers: SiteWiseUser[];
}

const HomeComponent: React.FC = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [filterType, setFilterType] = useState<'7days' | '30days' | 'lastyear'>('7days');
  
  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<ResponseProp>('vouchers/company-redeem-stats');

  const [
    postConnectedUsersData,
    connectedUsersResponse,
    connectedUsersError,
    connectedUsersLoading,
    connectedUsersMessage,
    connectedUsersErrorCount,
  ] = useAxiosPost<ResponseProp>('company-sites/total-connected-users');

  const [stats, setStats] = useState<StatsData[]>([]);
  const [totalConnectedUsers, setTotalConnectedUsers] = useState<number>(0);
  const [siteWiseUsers, setSiteWiseUsers] = useState<SiteWiseUser[]>([]);
  const [connectedUsersStatsPeriod, setConnectedUsersStatsPeriod] = useState<'daily' | 'yesterday' | 'weekly' | 'monthly'>('daily');
  const [connectedUsersStatsData, setConnectedUsersStatsData] = useState<any>(null);
  const postApiDataRef = useRef(postApiData);
  const postConnectedUsersDataRef = useRef(postConnectedUsersData);
  const lastProcessedErrorCount = useRef(0);
  const lastProcessedConnectedUsersErrorCount = useRef(0);

  const [
    postConnectedUsersStatsData,
    connectedUsersStatsResponse,
    connectedUsersStatsError,
    connectedUsersStatsLoading,
    connectedUsersStatsMessage,
    connectedUsersStatsErrorCount,
  ] = useAxiosPost<ResponseProp>('company-sites/connected-users-stats');

  // Keep refs updated
  useEffect(() => {
    postApiDataRef.current = postApiData;
    postConnectedUsersDataRef.current = postConnectedUsersData;
  }, [postApiData, postConnectedUsersData]);

  // Fetch stats when filter type changes
  useEffect(() => {
    postApiDataRef.current({ filterType });
  }, [filterType]);

  // Fetch total connected users on component mount
  useEffect(() => {
    postConnectedUsersDataRef.current({});
  }, []);

  // Fetch connected users stats when period changes
  useEffect(() => {
    postConnectedUsersStatsData({ period: connectedUsersStatsPeriod });
  }, [connectedUsersStatsPeriod]);

  // Handle API response
  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: StatsResponse = apiResponse.responseData as StatsResponse;
      if (responseData && Array.isArray(responseData.stats)) {
        setStats(responseData.stats);
      } else {
        setStats([]);
      }
    }
  }, [apiResponse]);

  // Handle connected users response
  useEffect(() => {
    if (connectedUsersResponse !== null) {
      const responseData: ConnectedUsersResponse = connectedUsersResponse.responseData as ConnectedUsersResponse;
      if (responseData) {
        setTotalConnectedUsers(responseData.totalConnectedUsers || 0);
        setSiteWiseUsers(responseData.siteWiseUsers || []);
      } else {
        setTotalConnectedUsers(0);
        setSiteWiseUsers([]);
      }
    }
  }, [connectedUsersResponse]);

  // Handle connected users stats response
  useEffect(() => {
    if (connectedUsersStatsResponse !== null) {
      const responseData: any = connectedUsersStatsResponse.responseData;
      setConnectedUsersStatsData(responseData);
    }
  }, [connectedUsersStatsResponse]);

  // Handle API errors
  useEffect(() => {
    if (
      apiError !== null &&
      apiErrorCount !== 0 &&
      lastProcessedErrorCount.current !== apiErrorCount
    ) {
      lastProcessedErrorCount.current = apiErrorCount;
      openNotification('error', 'Failed to Load Statistics', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle connected users errors
  useEffect(() => {
    if (
      connectedUsersError !== null &&
      connectedUsersErrorCount !== 0 &&
      lastProcessedConnectedUsersErrorCount.current !== connectedUsersErrorCount
    ) {
      lastProcessedConnectedUsersErrorCount.current = connectedUsersErrorCount;
      openNotification('error', 'Failed to Load Connected Users', connectedUsersError);
    }
  }, [connectedUsersError, connectedUsersErrorCount, openNotification]);

  // Format dates for display
  const formatDateForDisplay = (dateStr: string, isMonth: boolean = false): string => {
    if (isMonth) {
      // Format: YYYY-MM -> "Jan 2024"
      const [year, month] = dateStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return moment(date).format('MMM YYYY');
    } else {
      // Format: YYYY-MM-DD -> "15 Jan"
      const date = new Date(dateStr);
      const day = date.getDate();
      const month = moment(date).format('MMM');
      return `${day} ${month}`;
    }
  };

  // Prepare chart data
  const categories = stats.map((stat) => 
    formatDateForDisplay(stat.date, filterType === 'lastyear')
  );
  const seriesData = stats.map((stat) => stat.count);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: filterType === 'lastyear' ? '60%' : '55%',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    xaxis: {
      categories: categories,
      title: {
        text: filterType === 'lastyear' ? 'Month' : 'Date',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      title: {
        text: 'Vouchers Redeemed',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
      labels: {
        formatter: (value: number) => {
          return Math.floor(value).toString();
        },
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: ['#3A77BC'],
        inverseColors: false,
        opacityFrom: 0.85,
        opacityTo: 0.85,
        stops: [0, 100],
      },
    },
    colors: ['#3A77BC'],
    tooltip: {
      y: {
        formatter: (value: number) => {
          return `${value} voucher${value !== 1 ? 's' : ''} redeemed`;
        },
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  };

  const chartSeries = [
    {
      name: 'Vouchers Redeemed',
      data: seriesData,
    },
  ];

  // Prepare site-wise users chart data
  const siteNames = siteWiseUsers.map((site: SiteWiseUser) => site.siteName);
  const siteUserCounts = siteWiseUsers.map((site: SiteWiseUser) => site.connectedUsers);

  const siteWiseChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '55%',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    xaxis: {
      categories: siteNames,
      title: {
        text: 'Site',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      title: {
        text: 'Connected Users',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
      labels: {
        formatter: (value: number) => {
          return Math.floor(value).toString();
        },
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: ['#52c41a'],
        inverseColors: false,
        opacityFrom: 0.85,
        opacityTo: 0.85,
        stops: [0, 100],
      },
    },
    colors: ['#52c41a'],
    tooltip: {
      y: {
        formatter: (value: number) => {
          return `${value} user${value !== 1 ? 's' : ''} connected`;
        },
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  };

  const siteWiseChartSeries = [
    {
      name: 'Connected Users',
      data: siteUserCounts,
    },
  ];

  const getChartTitle = () => {
    switch (filterType) {
      case '7days':
        return 'Voucher Redeem Statistics - Last 7 Days';
      case '30days':
        return 'Voucher Redeem Statistics - Last 30 Days';
      case 'lastyear':
        return 'Voucher Redeem Statistics - Last 12 Months';
      default:
        return 'Voucher Redeem Statistics';
    }
  };

  // Format dates for connected users stats chart
  const formatDateForConnectedUsersStats = (dateStr: string, isHour: boolean = false): string => {
    if (!dateStr) return '';
    if (isHour) {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const day = date.getDate();
      const month = moment(date).format('MMM');
      return `${day} ${month}`;
    }
  };

  // Prepare connected users stats chart data
  let connectedUsersCategories: string[] = [];
  let connectedUsersSeriesData: any[] = [];
  let connectedUsersChartOptions: ApexOptions | null = null;

  if (connectedUsersStatsData && connectedUsersStatsData.stats && Array.isArray(connectedUsersStatsData.stats) && connectedUsersStatsData.stats.length > 0) {
    if (connectedUsersStatsPeriod === 'daily' || connectedUsersStatsPeriod === 'yesterday') {
      const validStats = connectedUsersStatsData.stats.filter(
        (item: any) => item && item.hour && (item.connectedUsers !== null && item.connectedUsers !== undefined)
      );
      
      if (validStats.length > 0) {
        connectedUsersCategories = validStats.map((item: any) => formatDateForConnectedUsersStats(item.hour, true));
        connectedUsersSeriesData = [
          {
            name: 'Connected Users',
            data: validStats.map((item: any) => {
              const value = Number(item.connectedUsers);
              return isNaN(value) ? 0 : value;
            }),
          },
        ];

        connectedUsersChartOptions = {
          chart: {
            type: 'line',
            height: 350,
            toolbar: {
              show: false,
            },
          },
          stroke: {
            curve: 'smooth',
            width: 3,
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '12px',
              colors: ['#304758'],
            },
          },
          xaxis: {
            categories: connectedUsersCategories,
            title: {
              text: 'Time',
              style: {
                fontSize: '14px',
                fontWeight: 600,
              },
            },
          },
          yaxis: {
            title: {
              text: 'Connected Users',
              style: {
                fontSize: '14px',
                fontWeight: 600,
              },
            },
            labels: {
              formatter: (value: number) => {
                return Math.floor(value).toString();
              },
            },
          },
          fill: {
            type: 'gradient',
            gradient: {
              shade: 'light',
              type: 'vertical',
              shadeIntensity: 0.25,
              gradientToColors: ['#3A77BC'],
              inverseColors: false,
              opacityFrom: 0.85,
              opacityTo: 0.85,
              stops: [0, 100],
            },
          },
          colors: ['#3A77BC'],
          tooltip: {
            y: {
              formatter: (value: number) => {
                return `${value} user${value !== 1 ? 's' : ''}`;
              },
            },
          },
          grid: {
            borderColor: '#e0e0e0',
            strokeDashArray: 3,
          },
        };
      }
    } else {
      const validStats = connectedUsersStatsData.stats.filter(
        (item: any) =>
          item &&
          item.date &&
          (item.minConnectedUsers !== null && item.minConnectedUsers !== undefined) &&
          (item.maxConnectedUsers !== null && item.maxConnectedUsers !== undefined)
      );
      
      if (validStats.length > 0) {
        connectedUsersCategories = validStats.map((item: any) => formatDateForConnectedUsersStats(item.date, false));
        connectedUsersSeriesData = [
          {
            name: 'Minimum Connected Users',
            data: validStats.map((item: any) => {
              const value = Number(item.minConnectedUsers);
              return isNaN(value) ? 0 : value;
            }),
            color: '#ff4d4f',
          },
          {
            name: 'Maximum Connected Users',
            data: validStats.map((item: any) => {
              const value = Number(item.maxConnectedUsers);
              return isNaN(value) ? 0 : value;
            }),
            color: '#52c41a',
          },
        ];

        connectedUsersChartOptions = {
          chart: {
            type: 'bar',
            height: 350,
            toolbar: {
              show: false,
            },
          },
          plotOptions: {
            bar: {
              borderRadius: 4,
              horizontal: false,
              columnWidth: '55%',
              dataLabels: {
                position: 'top',
              },
            },
          },
          dataLabels: {
            enabled: true,
            offsetY: -20,
            style: {
              fontSize: '12px',
              colors: ['#304758'],
            },
          },
          xaxis: {
            categories: connectedUsersCategories,
            title: {
              text: 'Date',
              style: {
                fontSize: '14px',
                fontWeight: 600,
              },
            },
          },
          yaxis: {
            title: {
              text: 'Connected Users',
              style: {
                fontSize: '14px',
                fontWeight: 600,
              },
            },
            labels: {
              formatter: (value: number) => {
                return Math.floor(value).toString();
              },
            },
          },
          fill: {
            type: 'gradient',
            gradient: {
              shade: 'light',
              type: 'vertical',
              shadeIntensity: 0.25,
              gradientToColors: ['#3A77BC'],
              inverseColors: false,
              opacityFrom: 0.85,
              opacityTo: 0.85,
              stops: [0, 100],
            },
          },
          colors: ['#ff4d4f', '#52c41a'],
          tooltip: {
            y: {
              formatter: (value: number) => {
                return `${value} user${value !== 1 ? 's' : ''}`;
              },
            },
          },
          grid: {
            borderColor: '#e0e0e0',
            strokeDashArray: 3,
          },
          legend: {
            position: 'top',
            horizontalAlign: 'right',
          },
        };
      }
    }
  }

  return (
    <>
      {contextHolder}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          {connectedUsersLoading ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            </Card>
          ) : (
            <DashboardBoxItem
              icon={OnlineUsersIcon}
              title="Total Connected Users"
              counterValue={totalConnectedUsers}
            />
          )}
        </Col>
      </Row>
      <Card title="Connected Users by Site" style={{ marginBottom: '24px' }}>
        {connectedUsersLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <Typography style={{ marginTop: '16px', display: 'block' }}>
              Loading connected users...
            </Typography>
          </div>
        ) : connectedUsersError ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty
              description={
                <Typography.Text type="danger">
                  {connectedUsersError || 'Failed to load connected users'}
                </Typography.Text>
              }
            />
          </div>
        ) : siteWiseUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty description="No connected users data available." />
          </div>
        ) : (
          <ReactApexChart
            options={siteWiseChartOptions}
            series={siteWiseChartSeries}
            type="bar"
            height={350}
          />
        )}
      </Card>
      <Card
        title="Company Connected Users Statistics"
        extra={
          <Radio.Group
            value={connectedUsersStatsPeriod}
            onChange={(e) => setConnectedUsersStatsPeriod(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="daily">Daily</Radio.Button>
            <Radio.Button value="yesterday">Yesterday</Radio.Button>
            <Radio.Button value="weekly">Weekly</Radio.Button>
            <Radio.Button value="monthly">Monthly</Radio.Button>
          </Radio.Group>
        }
        style={{ marginBottom: '24px' }}
      >
        {connectedUsersStatsLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <Typography style={{ marginTop: '16px', display: 'block' }}>Loading statistics...</Typography>
          </div>
        ) : connectedUsersStatsError ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty
              description={
                <Typography.Text type="danger">
                  {connectedUsersStatsError || 'Failed to load statistics'}
                </Typography.Text>
              }
            />
          </div>
        ) : !connectedUsersStatsData || !connectedUsersStatsData.stats || connectedUsersStatsData.stats.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty description="No statistics data available for the selected period." />
          </div>
        ) : !connectedUsersChartOptions || connectedUsersCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty description="No valid data available for the chart" />
          </div>
        ) : (
          <div>
            {/* Min/Max Labels for Daily and Yesterday */}
            {(connectedUsersStatsPeriod === 'daily' || connectedUsersStatsPeriod === 'yesterday') &&
              connectedUsersStatsData.minConnectedUsers !== null &&
              connectedUsersStatsData.maxConnectedUsers !== null && (
                <div
                  style={{ marginBottom: '16px', display: 'flex', gap: '24px', justifyContent: 'center' }}
                >
                  <Typography.Text strong style={{ color: '#52c41a' }}>
                    Maximum: {connectedUsersStatsData.maxConnectedUsers} users
                  </Typography.Text>
                  <Typography.Text strong style={{ color: '#ff4d4f' }}>
                    Minimum: {connectedUsersStatsData.minConnectedUsers} users
                  </Typography.Text>
                </div>
              )}
            <ReactApexChart
              options={connectedUsersChartOptions}
              series={connectedUsersSeriesData}
              type={connectedUsersStatsPeriod === 'daily' || connectedUsersStatsPeriod === 'yesterday' ? 'line' : 'bar'}
              height={350}
            />
          </div>
        )}
      </Card>
      <Card
        title={getChartTitle()}
        extra={
          <Radio.Group
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="7days">7 Days</Radio.Button>
            <Radio.Button value="30days">30 Days</Radio.Button>
            <Radio.Button value="lastyear">Last Year</Radio.Button>
          </Radio.Group>
        }
      >
        {apiLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <Typography style={{ marginTop: '16px', display: 'block' }}>
              Loading statistics...
            </Typography>
          </div>
        ) : apiError ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty
              description={
                <Typography.Text type="danger">
                  {apiError || 'Failed to load statistics'}
                </Typography.Text>
              }
            />
          </div>
        ) : stats.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty description="No redeem data available for the selected period." />
          </div>
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={350}
          />
        )}
      </Card>
    </>
  );
};

export default HomeComponent;
